import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { FaChevronDown, FaTimes, FaCheck, FaSearch, FaCheckDouble, FaTimesCircle } from "react-icons/fa";
import Clickable from "./Clickable";

/**
 * Multi-select filter dropdown. (Copy từ crm/payment-request/common — dùng chung cho
 * Purchase Requisition + Purchase Order.)
 */
const FilterDropdown = ({ label, icon: Icon, value = [], options = [], onChange, align = "left" }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [query, setQuery] = useState("");
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);
  const searchRef = useRef(null);

  const valueArr = useMemo(() => (Array.isArray(value) ? value : value != null ? [value] : []), [value]);
  const selectedSet = useMemo(() => new Set(valueArr), [valueArr]);
  const active = valueArr.length > 0;
  const labelOf = (val) => options.find((o) => o.value === val)?.label ?? val;

  useEffect(() => {
    if (!open) { setQuery(""); return; }
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [options, query]);

  const showSearch = options.length >= 5;
  const buttonLabel = valueArr.length === 0 ? label : valueArr.length === 1 ? labelOf(valueArr[0]) : `${valueArr.length} selected`;

  const toggle = (val) => onChange(selectedSet.has(val) ? valueArr.filter((v) => v !== val) : [...valueArr, val]);
  const selectAllVisible = () => { const next = new Set(valueArr); filtered.forEach((o) => next.add(o.value)); onChange([...next]); };
  const clearAll = () => onChange([]);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const compute = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      const width = Math.max(260, rect.width);
      const left = align === "right" || rect.left + width > window.innerWidth - 8 ? Math.max(8, rect.right - width) : rect.left;
      setCoords({ top: rect.bottom + 6, left, width });
    };
    compute();
    const onMove = () => compute();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => { window.removeEventListener("scroll", onMove, true); window.removeEventListener("resize", onMove); };
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const inBtn = buttonRef.current?.contains(e.target);
      const inPop = popoverRef.current?.contains(e.target);
      if (!inBtn && !inPop) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const popover = open && coords && typeof document !== "undefined"
    ? createPortal(
        <div
          ref={popoverRef}
          style={{ position: "fixed", top: coords.top, left: coords.left, width: coords.width, minWidth: 260, zIndex: 100000 }}
          className="geist-scope tw-bg-geist-bg tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-md tw-shadow-geist-menu tw-overflow-hidden"
        >
          {showSearch && (
            <div className="tw-border-b tw-border-solid tw-border-geist-border tw-bg-geist-bg-2 tw-px-2.5 tw-py-2">
              <div className="tw-flex tw-items-center tw-gap-2 tw-px-2.5 tw-py-1.5 tw-bg-geist-bg tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-sm focus-within:tw-border-geist-accent">
                <FaSearch className="tw-text-geist-fg-3" size={11} />
                <input
                  ref={searchRef} type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search..."
                  className="tw-flex-1 tw-min-w-0 tw-bg-transparent tw-outline-none tw-border-0 tw-text-xs tw-text-geist-fg placeholder:tw-text-geist-fg-3"
                />
                {query && <Clickable className="tw-text-geist-fg-3 hover:tw-text-geist-fg" onClick={() => setQuery("")}><FaTimes size={11} /></Clickable>}
              </div>
            </div>
          )}

          <div className="tw-max-h-72 tw-overflow-y-auto tw-py-1.5">
            {filtered.length === 0 ? (
              <div className="tw-px-3 tw-py-4 tw-text-xs tw-text-geist-fg-3 tw-italic tw-text-center">No matches</div>
            ) : (
              filtered.map((o) => {
                const checked = selectedSet.has(o.value);
                return (
                  <Clickable
                    key={o.value}
                    onClick={() => toggle(o.value)}
                    title={o.label}
                    className={`tw-w-full tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-text-sm tw-transition-colors ${
                      checked ? "tw-bg-geist-100 tw-text-geist-fg tw-font-medium" : "tw-text-geist-fg-2 hover:tw-bg-geist-100"
                    }`}
                  >
                    <span className={`tw-w-4 tw-h-4 tw-rounded-geist-sm tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-border tw-border-solid ${
                      checked ? "tw-border-geist-accent tw-bg-geist-accent tw-text-white" : "tw-border-geist-border-2 tw-bg-geist-bg"
                    }`}>
                      {checked && <FaCheck size={9} />}
                    </span>
                    <span className="tw-truncate">{o.label}</span>
                  </Clickable>
                );
              })
            )}
          </div>

          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-py-2 tw-border-t tw-border-solid tw-border-geist-border tw-bg-geist-bg-2">
            <Clickable onClick={selectAllVisible} className="tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-semibold tw-text-geist-fg-2 hover:tw-text-geist-accent">
              <FaCheckDouble size={10} /> {query.trim() ? "Select visible" : "Select all"}
            </Clickable>
            <Clickable onClick={clearAll} disabled={valueArr.length === 0}
              className={`tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-semibold ${valueArr.length === 0 ? "tw-text-geist-fg-3" : "tw-text-geist-error hover:tw-opacity-80"}`}>
              <FaTimesCircle size={10} /> Clear ({valueArr.length})
            </Clickable>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <Clickable
        ref={buttonRef}
        onClick={() => setOpen((o) => !o)}
        className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-py-1.5 tw-px-3 tw-text-xs tw-rounded-geist-sm tw-border tw-border-solid tw-transition-colors ${
          active ? "tw-border-geist-accent tw-bg-geist-accent/5 tw-text-geist-accent tw-font-semibold" : "tw-border-geist-border tw-bg-geist-bg tw-text-geist-fg-2 hover:tw-bg-geist-100"
        }`}
      >
        {Icon && <Icon size={11} />}
        <span className="tw-max-w-[160px] tw-truncate">{buttonLabel}</span>
        {active ? (
          <span onClick={(e) => { e.stopPropagation(); onChange([]); setOpen(false); }} className="-tw-mr-0.5 hover:tw-text-geist-error"><FaTimes size={12} /></span>
        ) : (
          <FaChevronDown size={9} className="tw-text-geist-fg-3" />
        )}
      </Clickable>
      {popover}
    </>
  );
};

FilterDropdown.propTypes = {
  label: PropTypes.node,
  icon: PropTypes.elementType,
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    PropTypes.string,
    PropTypes.number,
  ]),
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.node,
  })),
  onChange: PropTypes.func.isRequired,
  align: PropTypes.oneOf(["left", "right"]),
};

export default FilterDropdown;
